'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Wrench, Sparkles, Shield, Trees } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { servicesTranslations } from '@/translations/services'
import { Listbox } from '@headlessui/react'
import { ChevronDown } from 'lucide-react'

interface Service {
  id: string
  icon: React.ComponentType<{ className?: string }>
  image: string
}

const services: Service[] = [
  {
    id: 'maintenance',
    icon: Sparkles,
    image: '/images/services/garden-maintenance.webp',
  },
  {
    id: 'seasonal',
    icon: Shield,
    image: '/images/seasons/spring.webp',
  },
  {
    id: 'planting',
    icon: Wrench,
    image: '/images/services/garden-design.webp',
  },
  {
    id: 'tidying',
    icon: Shield,
    image: '/images/transformations/before-1.webp',
  },
  {
    id: 'treeSurgery',
    icon: Trees,
    image: '/images/services/tree-surgery.webp',
  }
]

export default function Services() {
  const { language } = useLanguage()
  const t = servicesTranslations[language]
  const [activeService, setActiveService] = useState(services[0])

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-white to-teal-50/15">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            {language === 'en' && t.title.includes('Services') ? (
              <>
                {t.title.split('Services')[0]}
                Services
                {t.title.split('Services')[1]}
              </>
            ) : language === 'cy' && t.title.includes('Gwasanaethau') ? (
              <>
                {t.title.split('Gwasanaethau')[0]}
                Gwasanaethau
                {t.title.split('Gwasanaethau')[1]}
              </>
            ) : t.title}
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Services Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Service Navigation - Desktop */}
          <div className="hidden lg:block space-y-4">
            {services.map((service) => (
              <motion.button
                key={service.id}
                onClick={() => setActiveService(service)}
                className={`group w-full p-6 rounded-2xl text-left transition-all duration-300 focus:outline-none ${
                  activeService.id === service.id
                    ? 'bg-white shadow-xl border-2 border-gray-200'
                    : 'bg-white/30 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 hover:shadow-md border border-gray-200/50 text-gray-500'
                }`}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                    activeService.id === service.id ? 'bg-gray-100' : 'bg-gray-100/50'
                  }`}>
                    <service.icon className={`w-8 h-8 transition-colors duration-200 ${
                      activeService.id === service.id ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-900'
                    }`} />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 text-sharp transition-colors duration-200 ${
                      activeService.id === service.id ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-900'
                    }`}>
                      {t.services[service.id].title}
                    </h3>
                    <p className={`text-sharp transition-colors duration-200 ${
                      activeService.id === service.id ? 'text-gray-600' : 'text-gray-500 group-hover:text-gray-600'
                    }`}>
                      {t.services[service.id].description}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Service Navigation - Mobile/Tablet */}
          <div className="lg:hidden mb-8 relative">
            <Listbox value={activeService.id} onChange={(id: string) => {
              const found = services.find(s => s.id === id)
              if (found) setActiveService(found)
            }}>
              {({ open }: { open: boolean }) => (
                <div>
                  <Listbox.Button className={`w-full p-4 text-left bg-white rounded-xl shadow-sm focus:outline-none transition-all duration-200 border ${open ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium text-gray-900">{t.services[activeService.id].title}</span>
                      <ChevronDown className="h-5 w-5 text-gray-600" aria-hidden="true" />
                    </div>
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-sm border border-gray-100 max-h-60 overflow-auto focus:outline-none">
                    {services.map(service => (
                      <Listbox.Option
                        key={service.id}
                        value={service.id}
                        className={({ active }: { active: boolean }) =>
                          `relative cursor-pointer select-none px-6 py-4 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                        }
                      >
                        {({ selected }: { selected: boolean }) => (
                          <span className={`block text-lg font-medium ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{t.services[service.id].title}</span>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              )}
            </Listbox>
          </div>

          {/* Service Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeService.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
            >
              <div className="p-8">
                {/* Service Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                    <activeService.icon className="w-8 h-8 text-gray-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 text-sharp">
                      {t.services[activeService.id].title}
                    </h3>
                    <p className="text-gray-700 font-medium text-sharp">
                      {t.professionalService}
                    </p>
                  </div>
                </div>

                {/* Service Description */}
                <div className="mb-8">
                  <p className="text-gray-600 text-lg leading-relaxed text-sharp">
                    {t.services[activeService.id].description}
                  </p>
                </div>

                {/* What's Included */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 -mx-8">
                  <h4 className="font-semibold text-gray-900 mb-4 text-sharp">{t.whatsIncluded}</h4>
                  <ul className="space-y-3">
                    {t.services[activeService.id].features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start text-gray-700 text-sharp">
                        <svg className="w-5 h-5 text-gray-600 mr-3 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Section - Standout Dark Section */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 px-8 py-6 text-white rounded-b-3xl -mx-8 -mb-8">
                  <div className="flex flex-col items-start justify-start gap-4">
                    <div className="w-full">
                      <p className="text-gray-300 text-sharp mb-2">
                        {t.cta.text}
                      </p>
                      <p className="text-emerald-400 font-medium text-sm text-sharp">
                        {language === 'en'
                          ? 'Free consultation • No obligation • Professional service'
                          : 'Ymgynghoriad am ddim • Dim rhwymedigaeth • Gwasanaeth proffesiynol'}
                      </p>
                    </div>
                    <a
                      href="#contact"
                      className="inline-flex items-center px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg"
                    >
                      {t.cta.button}
                      <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}

/* 
Image URLs to download from Unsplash:

1. Garden Design (garden-design.jpg):
https://unsplash.com/photos/beautiful-garden-with-flowers-and-trees-during-daytime-8eH5yJ0PzUY

2. Garden Maintenance (garden-maintenance.jpg):
https://unsplash.com/photos/person-holding-green-plant-8eH5yJ0PzUY

3. Tree Surgery (tree-surgery.jpg):
https://unsplash.com/photos/person-cutting-tree-branch-with-saw-8eH5yJ0PzUY

4. Landscaping (landscaping.jpg):
https://unsplash.com/photos/beautiful-garden-with-stone-path-and-flowers-8eH5yJ0PzUY

Note: All images from Unsplash are free to use for commercial and noncommercial purposes.
No attribution is required, but it's appreciated.
*/ 